/**
 * BlockDispatcher — 类型分派器。
 *
 * 根据 DocumentBlock.type 路由到具体的块组件。
 * 不使用 BlockRendererContext，直接 import 块组件。
 */

import type { DocumentBlock, ForBlock as ForBlockType } from "@magic-md/core";
import { memo } from "react";
import { areDocumentBlocksRenderEqual } from "../../../lib/document-block-equality";
import { AnnotationBlockTarget } from "../../annotations/AnnotationBlockTarget";
import { useScopeContext } from "../../ScopeContext";
import { InlineRef } from "./InlineRef";
import { InlineWrapper } from "./InlineWrapper";
import { ForBlock } from "./ForBlock";
import { MarkdownBlock } from "./MarkdownBlock";
import { PluginBlock } from "./PluginBlock";

export const BlockDispatcher = memo(
    function BlockDispatcher({
        block,
        blockIndex,
    }: {
        block: DocumentBlock;
        blockIndex: number;
    }) {
        // Scope context: non-null when inside a for-iteration or content block
        const scope = useScopeContext();

        switch (block.type) {
            case "markdown":
                return (
                    <MarkdownBlock
                        content={block.content}
                        blockIndex={blockIndex}
                        source={block.source}
                    />
                );
            case "definition":
                return null;
            case "block-render":
                return (
                    <AnnotationBlockTarget
                        blockId={`render:${block.definitionId}`}
                        label={block.definitionId}
                    >
                        <div data-toc-id={`toc-render-${block.definitionId}`}>
                            <PluginBlock
                                definitionId={block.definitionId}
                                attrs={block.attrs}
                                mode="block"
                                defaultExpanded={false}
                            />
                        </div>
                    </AnnotationBlockTarget>
                );
            case "inline-render":
                return (
                    <InlineWrapper definitionId={block.definitionId}>
                        <PluginBlock
                            definitionId={block.definitionId}
                            attrs={block.attrs}
                            mode="inline"
                        />
                    </InlineWrapper>
                );
            case "inline-ref":
                return (
                    <InlineRef
                        definitionId={block.definitionId}
                        accessor={block.accessor}
                        outputPath={block.outputPath}
                        refPath={block.refPath}
                    />
                );
            case "for-block":
                // Top-level: hidden (rendered via ::render[forId]:: → PluginBlock → ForBlock)
                // Nested (inside scope): render inline so nested for-blocks display iterations
                return scope
                    ? <ForBlock block={block as ForBlockType} mode="block" />
                    : null;
            default:
                return null;
        }
    },
    (prev, next) => {
        if (prev.blockIndex !== next.blockIndex) return false;
        return areDocumentBlocksRenderEqual(prev.block, next.block);
    },
);
